/* Hand-rolled glTF 2.0 / GLB reader.

   Parses a .glb ArrayBuffer into plain geometry arrays and material specs that a
   caller turns into THREE objects. Scope: static meshes, PBR metallic-roughness
   with embedded textures. Skinning, animation, morph targets and Draco are out.

   Now that the project has a bundler, three's own GLTFLoader is available and
   covers all of the above. This module stays because it is known-good and has no
   dependencies at all — swapping is a deliberate later decision, not something to
   do in the middle of a refactor.

   Lifted out of its IIFE unchanged. The one rename: what callers reached through
   GLB._b64 is now the named export b64ToBytes. */

/* Hand-rolled glTF 2.0 / GLB reader — no three.js, no examples/jsm, no deps.
   Parses a .glb ArrayBuffer into plain geometry arrays and material specs that a
   caller turns into THREE objects. Scope: static meshes, PBR metallic-roughness
   with embedded textures. Skinning, animation, morph targets and Draco are out. */

const COMP = {5120:Int8Array,5121:Uint8Array,5122:Int16Array,5123:Uint16Array,5125:Uint32Array,5126:Float32Array};
const NUM  = {SCALAR:1,VEC2:2,VEC3:3,VEC4:4,MAT2:4,MAT3:9,MAT4:16};

export function b64ToBytes(b64){
  const bin = (typeof atob === 'function')
    ? atob(b64)
    : Buffer.from(b64,'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for(let i=0;i<bin.length;i++) out[i] = bin.charCodeAt(i);
  return out;
}

/* GLB container: 12-byte header then 4-byte-aligned chunks (JSON, then BIN). */
export function parseGLB(arrayBuffer){
  const dv = new DataView(arrayBuffer);
  if(dv.getUint32(0,true) !== 0x46546C67) throw new Error('Not a GLB file (bad magic).');
  const version = dv.getUint32(4,true);
  if(version !== 2) throw new Error('Only glTF 2.0 GLB is supported (got version '+version+').');
  const total = dv.getUint32(8,true);
  let off = 12, json = null, bin = null;
  while(off < total){
    const len  = dv.getUint32(off,true);
    const type = dv.getUint32(off+4,true);
    const start = off + 8;
    if(type === 0x4E4F534A)      json = JSON.parse(new TextDecoder().decode(new Uint8Array(arrayBuffer, start, len)));
    else if(type === 0x004E4942) bin  = new Uint8Array(arrayBuffer, start, len);
    off = start + len;
  }
  if(!json) throw new Error('GLB has no JSON chunk.');
  return { json, bin };
}

/* Also accept a plain .gltf JSON with a base64 data-URI buffer (no BIN chunk). */
export function resolveBuffers(json, bin){
  return (json.buffers || []).map((b,i)=>{
    if(b.uri == null){
      if(!bin) throw new Error('Buffer '+i+' expects the GLB binary chunk, which is missing.');
      return bin;
    }
    const m = /^data:.*?;base64,(.*)$/.exec(b.uri);
    if(!m) throw new Error('External buffer URIs are not supported: '+b.uri.slice(0,40));
    return b64ToBytes(m[1]);
  });
}

function readComp(dv, ctype, p){
  switch(ctype){
    case 5120: return dv.getInt8(p);
    case 5121: return dv.getUint8(p);
    case 5122: return dv.getInt16(p,true);
    case 5123: return dv.getUint16(p,true);
    case 5125: return dv.getUint32(p,true);
    case 5126: return dv.getFloat32(p,true);
    default: throw new Error('Unknown componentType '+ctype);
  }
}

function readAccessor(json, buffers, idx){
  const acc = json.accessors[idx];
  if(acc.bufferView == null){                 // sparse-only / all-zero accessor
    const n = NUM[acc.type];
    return { array: new Float32Array(acc.count * n), itemSize: n };
  }
  const bv   = json.bufferViews[acc.bufferView];
  const comp = COMP[acc.componentType];
  const n    = NUM[acc.type];
  const bytes = buffers[bv.buffer];
  const dv   = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const base = (bv.byteOffset||0) + (acc.byteOffset||0);
  const stride = bv.byteStride || (n * comp.BYTES_PER_ELEMENT);
  const out  = new comp(acc.count * n);
  for(let i=0;i<acc.count;i++)
    for(let j=0;j<n;j++)
      out[i*n+j] = readComp(dv, acc.componentType, base + i*stride + j*comp.BYTES_PER_ELEMENT);
  return { array: out, itemSize: n, normalized: !!acc.normalized };
}

/* 4x4 * 4x4, column-major (glTF convention). */
function mul(a,b){
  const o = new Array(16);
  for(let c=0;c<4;c++) for(let r=0;r<4;r++){
    let s=0; for(let k=0;k<4;k++) s += a[k*4+r]*b[c*4+k];
    o[c*4+r]=s;
  }
  return o;
}
function trs(node){
  if(node.matrix) return node.matrix.slice();
  const t = node.translation||[0,0,0], r = node.rotation||[0,0,0,1], s = node.scale||[1,1,1];
  const [x,y,z,w]=r, x2=x+x,y2=y+y,z2=z+z;
  const xx=x*x2,xy=x*y2,xz=x*z2,yy=y*y2,yz=y*z2,zz=z*z2,wx=w*x2,wy=w*y2,wz=w*z2;
  return [
    (1-(yy+zz))*s[0], (xy+wz)*s[0],     (xz-wy)*s[0],     0,
    (xy-wz)*s[1],     (1-(xx+zz))*s[1], (yz+wx)*s[1],     0,
    (xz+wy)*s[2],     (yz-wx)*s[2],     (1-(xx+yy))*s[2], 0,
    t[0],             t[1],             t[2],             1
  ];
}

function imageSpec(json, buffers, imgIdx){
  if(imgIdx == null) return null;
  const img = json.images[imgIdx];
  if(img.uri){
    const m = /^data:(.*?);base64,(.*)$/.exec(img.uri);
    if(m) return { bytes: b64ToBytes(m[2]), mime: m[1] };
    return null;                               // external file URI — unsupported
  }
  if(img.bufferView != null){
    const bv = json.bufferViews[img.bufferView];
    const buf = buffers[bv.buffer];
    const start = buf.byteOffset + (bv.byteOffset||0);
    return { bytes: new Uint8Array(buf.buffer.slice(start, start + bv.byteLength)),
             mime: img.mimeType || 'image/png' };
  }
  return null;
}

function texImage(json, buffers, texIdx){
  if(texIdx == null || !json.textures) return null;
  const t = json.textures[texIdx];
  return imageSpec(json, buffers, t.source);
}

function materialSpec(json, buffers, matIdx){
  const m = (matIdx==null || !json.materials) ? {} : json.materials[matIdx];
  const pbr = m.pbrMetallicRoughness || {};
  const bct = pbr.baseColorTexture, mrt = pbr.metallicRoughnessTexture;
  return {
    name: m.name || null,
    color: pbr.baseColorFactor || [1,1,1,1],
    metalness: pbr.metallicFactor==null ? 1 : pbr.metallicFactor,
    roughness: pbr.roughnessFactor==null ? 1 : pbr.roughnessFactor,
    baseColorTex: bct ? texImage(json, buffers, bct.index) : null,
    mrTex:        mrt ? texImage(json, buffers, mrt.index) : null,
    normalTex:    m.normalTexture ? texImage(json, buffers, m.normalTexture.index) : null,
    emissive:     m.emissiveFactor || [0,0,0],
    emissiveTex:  m.emissiveTexture ? texImage(json, buffers, m.emissiveTexture.index) : null,
    doubleSided:  !!m.doubleSided,
    alphaMode:    m.alphaMode || 'OPAQUE',
    alphaCutoff:  m.alphaCutoff==null ? 0.5 : m.alphaCutoff
  };
}

/* Flatten the default scene into a list of primitives, each with its baked world
   matrix (column-major) and geometry arrays + a material spec. */
export function extractMeshes(json, buffers){
  const out = [];
  const sceneIdx = json.scene==null ? 0 : json.scene;
  const scene = (json.scenes && json.scenes[sceneIdx]) || {nodes:(json.nodes||[]).map((_,i)=>i)};
  const walk = (nodeIdx, parent)=>{
    const node = json.nodes[nodeIdx];
    const world = mul(parent, trs(node));
    if(node.mesh != null){
      for(const prim of json.meshes[node.mesh].primitives){
        if(prim.mode!=null && prim.mode!==4) continue;   // triangles only
        const a = prim.attributes || {};
        if(a.POSITION==null) continue;
        const pos = readAccessor(json, buffers, a.POSITION);
        const g = { matrix: world, positions: pos.array, tris: 0,
                    normals:null, uvs:null, indices:null,
                    material: materialSpec(json, buffers, prim.material) };
        if(a.NORMAL!=null)     g.normals = readAccessor(json, buffers, a.NORMAL).array;
        if(a.TEXCOORD_0!=null) g.uvs     = readAccessor(json, buffers, a.TEXCOORD_0).array;
        if(prim.indices!=null){
          g.indices = readAccessor(json, buffers, prim.indices).array;
          g.tris = g.indices.length/3;
        } else {
          g.tris = pos.array.length/9;
        }
        out.push(g);
      }
    }
    (node.children||[]).forEach(ci=>walk(ci, world));
  };
  const I = [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
  (scene.nodes||[]).forEach(ni=>walk(ni, I));
  return out;
}

/* Bounding box over every primitive, in world space, for auto recenter + fit. */
export function boundsOf(meshes){
  let mnx=Infinity,mny=Infinity,mnz=Infinity, mxx=-Infinity,mxy=-Infinity,mxz=-Infinity;
  for(const m of meshes){
    const p=m.positions, M=m.matrix;
    for(let i=0;i<p.length;i+=3){
      const x=p[i],y=p[i+1],z=p[i+2];
      const wx=M[0]*x+M[4]*y+M[8]*z+M[12];
      const wy=M[1]*x+M[5]*y+M[9]*z+M[13];
      const wz=M[2]*x+M[6]*y+M[10]*z+M[14];
      if(wx<mnx)mnx=wx; if(wy<mny)mny=wy; if(wz<mnz)mnz=wz;
      if(wx>mxx)mxx=wx; if(wy>mxy)mxy=wy; if(wz>mxz)mxz=wz;
    }
  }
  return { min:[mnx,mny,mnz], max:[mxx,mxy,mxz],
           center:[(mnx+mxx)/2,(mny+mxy)/2,(mnz+mxz)/2],
           size:[mxx-mnx,mxy-mny,mxz-mnz] };
}
